import { KaboodleToaster } from "components/common";
import Documents from "components/Documents/drawer/DocumentsDrawer";
import { useServiceDOI } from "components/DOIHeaderSelect/useServiceDOI";
import { serviceToInvoiceableEntity } from "components/Invoicing/invoiceAdapter";
import InvoiceContainer from "components/Invoicing/InvoiceContainer";
import Notes from "components/Notes";
import { serviceToNotesEntity } from "components/Notes/notesAdapter";
import { SECTION } from "constants/SettingsConstants";
import { BackgroundJobsDrawer } from "components/BackgroundJobsDrawer/BackgroundJobsDrawer";
import ConsultingAssignmentForm from "pages/consulting/ConsultingAssignmentForm";
import ConsultingNotesForm from "pages/consulting/ConsultingNotesForm";
import FMMedicalHistoryReview from "pages/future-medical/shared/medical-history/FMMedicalHistoryReview";
import FMRxHistory from "pages/future-medical/shared/medical-history/FMRxHistory";
import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { setCurrentSection } from "reducers/main";
import { usePatchClaimantByIdMutation } from "services/claimant-api";
import {
  useGetDocumentManagementFileByIdQuery,
  usePatchDocumentManagementFileByIdMutation,
} from "services/document-management-api";
import {
  usePatchDateOfInjuryByIdMutation,
  usePatchServiceDateOfInjuryByIdMutation,
} from "services/doi-api.ts";
import type {
  DocumentManagementDataType,
  MiscServiceType,
} from "types/DataTypes";
import { SERVICE_TYPE } from "types/DataTypes";
import { useToken } from "utils/context";
import { useDispatch } from "utils/redux";

import DocumentManagementClaimantInformation from "./DMClaimantInformation";
import DMClinicianNotes from "./DMClinicianNotes";
import DocumentManagemenPageContainer from "./DocumentManagementPageContainer";

const DocumentManagementSectionPage = () => {
  const dispatch = useDispatch();
  const { accessToken } = useToken();
  const { documentManagementFileId, dmSection } = useParams<{
    documentManagementFileId: string;
    dmSection: string;
  }>();

  const { data: documentManagementFile } =
    useGetDocumentManagementFileByIdQuery(
      { documentManagementFileId },
      { skip: !documentManagementFileId || !accessToken },
    );

  const documentManagementFileData = {
    ...documentManagementFile,
    type: SERVICE_TYPE.DOCUMENT_MANAGEMENT,
    claimant: documentManagementFile?.file?.claimants?.[0] || null,
  } as unknown as DocumentManagementDataType;

  const { setError, clearErrors } = useFormContext();
  const [patchClaimantById] = usePatchClaimantByIdMutation();
  const [patchDateOfInjuryById] = usePatchDateOfInjuryByIdMutation();
  const [patchServiceDateOfInjury] = usePatchServiceDateOfInjuryByIdMutation();
  const [patchDocumentManagementFileById] =
    usePatchDocumentManagementFileByIdMutation();
  const getField = (field: string) =>
    field?.includes(".") ? field.split(".")[1] : field;

  const { selectedServiceDoi } = useServiceDOI(documentManagementFileData);
  const handleSaveDocumentManagementFile = useCallback(
    async (field: string | null, value: unknown) => {
      if (documentManagementFileId && accessToken) {
        try {
          if (field?.startsWith?.("claimant.")) {
            // handle claimant fields
            await patchClaimantById({
              id: documentManagementFile.file.claimants[0].id,
              data: {
                [getField(field)]: value,
              },
            }).unwrap();
          } else if (field?.startsWith?.("date_of_injury.")) {
            // handle date of injury fields
            if (selectedServiceDoi?.date_of_injury_id) {
              await patchDateOfInjuryById({
                id: selectedServiceDoi.date_of_injury_id.toString(),
                data: {
                  [getField(field)]: value,
                },
              }).unwrap();
            } else {
              KaboodleToaster.error(
                "Cannot update DOI: selectedServiceDoi is null",
              );
              console.error("Cannot update DOI: selectedServiceDoi is null");
            }
          } else if (field?.startsWith?.("service_date_of_injury.")) {
            // handle service date of injury fields (accident_description, accepted_conditions, denied_conditions)
            if (selectedServiceDoi?.id) {
              await patchServiceDateOfInjury({
                id: selectedServiceDoi.id.toString(),
                data: {
                  [getField(field)]: value,
                },
              }).unwrap();
            } else {
              KaboodleToaster.error(
                "Cannot update Service DOI: selectedServiceDoi is null",
              );
              console.error(
                "Cannot update Service DOI: selectedServiceDoi is null",
              );
            }
          } else {
            await patchDocumentManagementFileById({
              documentManagementFileId: documentManagementFileId,
              data: {
                [field]: value,
              },
            }).unwrap();
          }
          clearErrors(field || "");
        } catch (error) {
          console.error("Error on saving...", error);
          setError(field || "", {
            type: error.status,
            message: error.data[field]?.[0] || "An error occurred",
          });
        }
      }
    },
    [
      documentManagementFile,

      patchClaimantById,
      patchDateOfInjuryById,
      patchServiceDateOfInjury,
      patchDocumentManagementFileById,
      documentManagementFileId,
      selectedServiceDoi,
      clearErrors,
      setError,
    ],
  );

  useEffect(() => {
    dispatch(setCurrentSection(SECTION.DOCUMENT_MANAGEMENT));
  }, [dispatch]);

  if (!documentManagementFile) {
    return null;
  }
  const entity = serviceToInvoiceableEntity(documentManagementFileData);

  const sectionPanels: Record<string, React.ReactNode> = {
    operations: (
      <>
        <div className="swim-headers">Assignment + Notes</div>
        <div className="swim-column">
          <ConsultingAssignmentForm
            service={documentManagementFileData as unknown as MiscServiceType}
            file={documentManagementFileData?.file}
          />
          <ConsultingNotesForm
            service={documentManagementFileData as unknown as MiscServiceType}
          />
        </div>
      </>
    ),
    invoice: (
      <>
        <div className="swim-headers">Invoice Details</div>
        <InvoiceContainer entity={entity} invoiceType="document_management" />
      </>
    ),
    notes: (
      <>
        <div className="swim-headers">Notes</div>
        {entity && (
          <Notes entity={serviceToNotesEntity(documentManagementFileData)} />
        )}
      </>
    ),
  };

  const rightPanelContent = <div>{sectionPanels[dmSection]}</div>;

  return (
    <DocumentManagemenPageContainer
      documentManagementFile={documentManagementFileData}
      layout="split"
    >
      <DocumentManagementClaimantInformation
        onSave={handleSaveDocumentManagementFile}
        service={documentManagementFileData}
        doiId={selectedServiceDoi?.date_of_injury_id || null}
      />
      {rightPanelContent}
      <FMMedicalHistoryReview service={documentManagementFileData} />
      <FMRxHistory service={documentManagementFileData} />
      <DMClinicianNotes
        service={documentManagementFileData}
        onSave={handleSaveDocumentManagementFile}
      />
      <Documents
        fileId={documentManagementFile?.file?.id}
        serviceId={documentManagementFile?.id}
        serviceType="docs"
      />
      {documentManagementFileId && (
        <BackgroundJobsDrawer
          filters={{ document_management_file_id: documentManagementFileId }}
        />
      )}
    </DocumentManagemenPageContainer>
  );
};

export default DocumentManagementSectionPage;
